'use client'

import { useEffect, useState } from 'react'

import { trackCustomEvent } from '@/hooks/client/use-analytics-engine'
import { EventType } from '@/lib/analytics/events'
import type { BrandAccessoryFamily } from '@/lib/supabase/brand-modules/types'
import { tw } from '@/lib/utils'

export interface AccessoryFilterLabels {
  all: string
  /** "{{shown}} of {{total}} accessories compatible with {{family}}" */
  footer: string
  /** "{{count}} accessory" / "{{count}} accessories" for the group headers. */
  groupCountOne: string
  groupCountMany: string
}

interface AccessoryFamilyFilterProps {
  families: BrandAccessoryFamily[]
  totalCount: number
  /** Initial selection (the flagship). The server pre-renders the cards in
   *  this filtered state, so first paint and hydration agree. */
  defaultReleaseSlug: string | null
  labels: AccessoryFilterLabels
}

const fill = (template: string, args: Record<string, string | number>) =>
  template.replace(/\{\{(\w+)\}\}/g, (_, name) => String(args[name] ?? ''))

// The accessory module's family selector, defaulting to the flagship. The
// cards are server-rendered once (images stay out of the island) already in
// the default-filtered state; this island re-filters them in the DOM by the
// `data-accessory-releases` attribute, keeps the group-header counts honest,
// and hides groups that empty out. With JS disabled the flagship subset
// shows — the full catalog stays in the DOM.
export const AccessoryFamilyFilter = ({
  families,
  totalCount,
  defaultReleaseSlug,
  labels,
}: AccessoryFamilyFilterProps) => {
  const [selected, setSelected] = useState<string | null>(defaultReleaseSlug)
  const family = families.find((f) => f.releaseSlug === selected) ?? null

  useEffect(() => {
    const root = document.querySelector('[data-brand-accessories]')
    if (!root) {
      return undefined
    }
    const cards = Array.from(root.querySelectorAll<HTMLElement>('[data-accessory-card]'))
    for (const card of cards) {
      const releases = (card.dataset.accessoryReleases ?? '').split(' ')
      const visible = !selected || releases.includes(selected)
      card.style.display = visible ? '' : 'none'
    }
    for (const group of Array.from(root.querySelectorAll<HTMLElement>('[data-accessory-group]'))) {
      const visibleCount = Array.from(group.querySelectorAll<HTMLElement>('[data-accessory-card]')).filter(
        (card) => card.style.display !== 'none'
      ).length
      group.style.display = visibleCount === 0 ? 'none' : ''
      const count = group.querySelector<HTMLElement>('[data-accessory-group-count]')
      if (count) {
        count.textContent = fill(visibleCount === 1 ? labels.groupCountOne : labels.groupCountMany, {
          count: visibleCount,
        })
      }
    }
    return () => {
      for (const card of cards) {
        card.style.display = ''
      }
    }
  }, [selected, labels.groupCountOne, labels.groupCountMany])

  const select = (slug: string | null) => {
    setSelected((prev) => {
      const next = prev === slug ? null : slug
      if (next) {
        trackCustomEvent('brand-accessories--family', EventType.Click, { position: next })
      }
      return next
    })
  }

  return (
    <div>
      <ul className="border-navy/10 divide-navy/5 divide-y overflow-hidden rounded-2xl border bg-white">
        <li>
          <button
            type="button"
            aria-pressed={selected === null}
            data-tracking="ignore"
            onClick={() => select(null)}
            className={tw(
              'text-md flex min-h-11 w-full cursor-pointer items-center justify-between gap-3 px-4 text-left transition-colors',
              selected === null ? 'bg-navy font-semibold text-white' : 'text-navy hover:bg-navy/5 font-medium'
            )}
          >
            <span>{labels.all}</span>
            <span className={tw('text-sm tabular-nums', selected === null ? 'text-white/60' : 'text-navy/45')}>
              {totalCount}
            </span>
          </button>
        </li>
        {families.map((f) => {
          const active = selected === f.releaseSlug
          return (
            <li key={f.releaseSlug}>
              <button
                type="button"
                aria-pressed={active}
                data-tracking="ignore"
                onClick={() => select(f.releaseSlug)}
                className={tw(
                  'text-md flex min-h-11 w-full cursor-pointer items-center justify-between gap-3 px-4 text-left transition-colors',
                  active ? 'bg-navy font-semibold text-white' : 'text-navy hover:bg-navy/5 font-medium'
                )}
              >
                <span>{f.name}</span>
                <span className={tw('text-sm tabular-nums', active ? 'text-white/60' : 'text-navy/45')}>
                  {f.compatibleCount}
                </span>
              </button>
            </li>
          )
        })}
      </ul>
      {family && (
        <p className="text-navy/55 mt-3 text-sm leading-relaxed">
          {fill(labels.footer, { shown: family.compatibleCount, total: totalCount, family: family.name })}
        </p>
      )}
    </div>
  )
}
